// app/src/lib/editHistory.ts

/** A reversible editing operation on the in-progress draft/edit buffer. */
export interface EditCommand {
  /** Re-apply the change (used by redo). */
  apply: () => void;
  /** Reverse the change (used by undo). */
  invert: () => void;
  /** Human label, for debugging / future UI. */
  label: string;
}

/**
 * In-session undo/redo stack. Operates on the draft/edit buffer only — never on
 * persisted server state. Caller applies the change, then `push`es the command.
 */
export class EditHistory {
  private undoStack: EditCommand[] = [];
  private redoStack: EditCommand[] = [];

  /** Record an already-applied command; clears the redo stack. */
  push(cmd: EditCommand): void {
    this.undoStack.push(cmd);
    this.redoStack.length = 0;
  }

  undo(): void {
    const cmd = this.undoStack.pop();
    if (!cmd) return;
    cmd.invert();
    this.redoStack.push(cmd);
  }

  redo(): void {
    const cmd = this.redoStack.pop();
    if (!cmd) return;
    cmd.apply();
    this.undoStack.push(cmd);
  }

  clear(): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }
  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }
}
